const NO_TIMEZONE_REGEX = /^(\d{4}-\d{2}-\d{2})$/;
export const ONE_MINUTE_IN_MS = 1000 * 60;
export const ONE_HOUR_IN_MS = ONE_MINUTE_IN_MS * 60;
export const SIX_HOURS_IN_MS = ONE_HOUR_IN_MS * 6;
export const ONE_DAY_IN_MS = ONE_HOUR_IN_MS * 24;
export const ONE_WEEK_IN_SECONDS = 60 * 60 * 24 * 7;

export const formatTime = (timeInSeconds: number): string => {
  const formattedTime = new Date(Math.round(timeInSeconds) * 1000)
    .toISOString()
    .substring(11, 19);
  const hours = formattedTime.substring(0, 2);
  const moreThanOneHour = Number(hours) > 0;

  if (moreThanOneHour) {
    return formattedTime;
  }
  const timeWithoutHour = formattedTime.substring(3);
  return timeWithoutHour;
};

export const formatToPTBRDate = (
  date?: string | number | Date | null,
  options?: Intl.DateTimeFormatOptions
) => {
  if (!date || new Date(date).toString() === 'Invalid Date') {
    return '--';
  }

  const hasNoTimezone = NO_TIMEZONE_REGEX.test(String(date));
  if (hasNoTimezone) {
    date = date + 'T18:00:00.000+0000';
  }

  return new Date(date).toLocaleDateString('pt-br', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    ...options,
  }); //dd/mm/yyy
};

export const calculatePeriodFromDate = (
  period: FilterPeriods,
  referenceDate = Date.now()
): CalculatePeriodFromDate => {
  if (period === 'today') {
    const initialDate = new Date(referenceDate);
    const finalDate = new Date(referenceDate);
    initialDate.setHours(0, 0, 0);
    finalDate.setHours(23, 59, 59);
    return {
      initialDate: initialDate.getTime(),
      finalDate: finalDate.getTime(),
    };
  }
  const initialDate = referenceDate - subtractPeriod[period];
  return { initialDate, finalDate: referenceDate };
};

const subtractPeriod: Record<Exclude<FilterPeriods, 'today'>, number> = {
  week: ONE_DAY_IN_MS * 7,
  fortnight: ONE_DAY_IN_MS * 15,
  month: ONE_DAY_IN_MS * 30,
  quarter: ONE_DAY_IN_MS * 30 * 3,
  semester: ONE_DAY_IN_MS * 30 * 6,
};

export type FilterPeriods =
  | 'today'
  | 'week'
  | 'fortnight'
  | 'month'
  | 'quarter'
  | 'semester';

interface CalculatePeriodFromDate {
  initialDate: number;
  finalDate: number;
}

export const transformYearMonthToISOString = (dateString: string): string => {
  const year = parseInt(dateString.substring(0, 4), 10);
  const month = parseInt(dateString.substring(4, 6), 10) - 1; // Months are 0-based in JavaScript Date
  const date = new Date(year, month);

  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toISOString(); //2024-12-01T03:00:00.000Z
};

export const formatDateToPTBRMonth = (date: Date): string => {
  return date.toLocaleString('pt-BR', { month: 'long' }); // julho
};

export const formatDateToPTBRDayMonth = (date: Date): string => {
  const month = formatDateToPTBRMonth(date);
  const formattedDate = `${date.getDate()} ${month}`;
  return formattedDate; // 22 julho
};

export const formatDateToPTBRMonthYear = (
  date: Date,
  isCompact = false
): string => {
  const month = formatDateToPTBRMonth(date);
  const year = date.getFullYear();
  return isCompact ? `${month.slice(0, 3)}. ${year}` : `${month} de ${year}`; // julho de 2024
};

export const formatRelativeDateToPTBR = (
  date: Date,
  referenceDate = new Date(),
  withYear = true
): string => {
  if (isNaN(date.getTime())) return '--';

  const startOfDay = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();

  const daysAgo = Math.round(
    (startOfDay(referenceDate) - startOfDay(date)) / ONE_DAY_IN_MS
  );

  if (daysAgo === 0) return 'Hoje';
  if (daysAgo === 1) return 'Ontem';

  const month = formatDateToPTBRMonth(date);
  const isSameYear = date.getFullYear() === referenceDate.getFullYear();

  return isSameYear || !withYear
    ? `${date.getDate()} de ${month}` // 3 de agosto
    : `${date.getDate()} de ${month} de ${date.getFullYear()}`; // 3 de agosto de 2024
};

export const formatDateToPTBRString = (date: Date): string => {
  const month = formatDateToPTBRMonth(date);
  return `${date.getDate()} de ${month}, ${date.getFullYear()}`; // 22 de julho, 2024
};

export const parseDateGetTime = (dateStr: string) => {
  // dateStr: dd/mm/yyyy
  const [day, month, year] = dateStr.split('/').map(Number);
  return new Date(year, month - 1, day).getTime();
};

export const formatDateToReferenceYearMonth = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  return `${year}${month}`; // 202506
};

export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`; // 2025/07/22
};

export const termFromToday = (date: string) => {
  const agora = new Date();
  const dataFutura = new Date(date);

  if (isNaN(dataFutura.getTime())) {
    return '--';
  }
  if (isNaN(agora.getTime())) {
    return 'Data atual inválida';
  }

  let anos = dataFutura.getFullYear() - agora.getFullYear();
  let meses = dataFutura.getMonth() - agora.getMonth();
  let dias = dataFutura.getDate() - agora.getDate();

  if (dias < 0) {
    meses -= 1;

    const mesAnterior = new Date(
      dataFutura.getFullYear(),
      dataFutura.getMonth(),
      0
    );
    dias += mesAnterior.getDate();
  }

  if (meses < 0) {
    anos -= 1;
    meses += 12;
  }

  if (
    anos < 0 ||
    (anos === 0 && meses < 0) ||
    (anos === 0 && meses === 0 && dias < 0)
  )
    return 'Prazo vencido';

  const partes: string[] = [];
  if (anos > 0) partes.push(anos === 1 ? '1 ano' : `${anos} anos`);
  if (meses > 0) partes.push(meses === 1 ? '1 mês' : `${meses} meses`);
  if (anos === 0 && meses === 0 && dias > 0)
    partes.push(dias === 1 ? '1 dia' : `${dias} dias`);

  return partes.join(' e ') || 'Menos de 1 dia';
};

export const getPTBRDateAndRemoveYear = (date: string): string => {
  return date.slice(0, -5);
};

export const getCurrentAgeByBirthDate = (
  birthDate: string
): number | undefined => {
  if (typeof birthDate !== 'string') return undefined;

  const parts = birthDate.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return undefined;

  const [year, month, day] = parts;
  const hoje = new Date();
  let idade = hoje.getFullYear() - year;

  const mesAtual = hoje.getMonth() + 1;
  const diaAtual = hoje.getDate();

  if (mesAtual < month || (mesAtual === month && diaAtual < day)) {
    idade--;
  }

  return idade;
};

export const toYYYYMMDD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const diffDays = (a: Date, b: Date): number =>
  Math.round((b.getTime() - a.getTime()) / 86_400_000);

export const todayISO = (timezone: string): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: timezone }).format(new Date());

export const currentTimeLabel = (timezone: string): string => {
  const now = new Date();
  const time = new Intl.DateTimeFormat('pt-BR', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(now);
  const offset =
    new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  return `${time} (${offset})`;
};

export const convertDateDayMonthAndYearWithComma = (dateStr: string) => {
  const date = new Date(dateStr);
  const formatted = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  if (!date || new Date(date).toString() === 'Invalid Date') {
    return '';
  }

  return formatted.replaceAll(' de ', (match, offset, string) =>
    string.slice(offset + 1).includes(match) ? match : ', '
  );
};
